import {
    useState,
    useEffect,
    useMemo,
    useCallback,
    useRef,
    Fragment,
} from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import type { EventFromAsset } from '../api/types';
import {
    Box,
    Typography,
    Skeleton,
    Collapse,
    IconButton,
    Checkbox,
    Popover,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useEventMarkets } from '../api/hooks';
import { useMultiMarketProbability } from '../api/useMultiMarketProbability';
import { Badge, ProbBar, SectionCard } from '../components/shared';
import {
    ConvergenceChart,
    type MarketLineMeta,
    type CandleResolution,
    type ConvictionMetric,
    RESOLUTION_OPTIONS,
} from '../components/charts/convergence';
import {
    clampResolutionToBudget,
    bestResolutionForBudget,
    candleBudgetForSeries,
} from '../components/charts/chartResolution';
import { EventsPanel } from '../components/EventsPanel';
import { formatVolume, formatDaysRemaining } from '../utils/format';
import { assignMarketColors } from '../utils/marketColors';
import type {
    SignalItem,
    SignalOverlayData,
    EventMarketDetail,
} from '../api/types';
import { MARKET_SIGNAL_TYPES } from '../api/types';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { AssetSearchOverlay } from '../components/AssetSearchOverlay';
import type { SelectedAsset } from '../components/AssetSearchOverlay';
import { useAssetPriceOverlay } from '../hooks/useAssetPriceOverlay';
import { TraderInsights } from '../components/TraderInsights';
import {
    useMarketSignals,
    useEventSignals,
    invalidateSignalsCache,
} from '../api/useSignals';
import type { MarketTrade } from '../api/useMarketTrades';
import type { TradeOverlayData } from '../components/charts/convergence';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useValueFlash } from '../../../hooks/useValueFlash';
import {
    VALUE_FLASH_UP_SX,
    VALUE_FLASH_DOWN_SX,
} from '../../../styles/realtimeAnimations';
import { hoverScrollbar } from '../../../styles/hoverScrollbar';
import { LiveTradesSidebarView } from '../components/LiveTradesSidebar';
import { useEventTradeSelection } from '../hooks/useEventTradeSelection';
import { useEventTrades } from '../api/useEventTrades';
import { useMarketsOutcomeSeries } from '../api/useMarketOutcomeSeries';
import { SidebarShell, useSidebarPanel } from '../components/SidebarShell';
import { SignalsPanel } from '../components/SignalsPanel';
import { AlertsPanel } from '../components/AlertsPanel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import InsightsIcon from '@mui/icons-material/Insights';
import { useConvictionTimeseries } from '../api/useConvictionTimeseries';
import type {
    ConvictionResolution,
    ConvictionSeries,
    ConvictionTimePoint,
} from '../api/useConvictionTimeseries';
import { useLatestConvictionByMarket } from '../api/useLatestConvictionByMarket';
import {
    ConvictionCircles,
    type RowConvictionCircle,
} from '../components/ConvictionCircles';
import {
    splitMarkets,
    closedRank,
    byVolumeDesc,
    byProbabilityDesc,
} from './defaultMarketSelection';
import { useEventMarketSort } from '../hooks/useEventMarketSort';
import {
    assetOverlayEndDate,
    chartEndDate,
    startOfUtcDay,
    tradesEndDate,
} from './eventDataWindow';

// Local hooks
import { useLiveTrades } from '../hooks/useLiveTrades';
import { useMarketSelection } from '../hooks/useMarketSelection';
import { useTraderOverlay } from '../hooks/useTraderOverlay';
import { useSelectedTraderRecentTrades } from '../api/useSelectedTraderRecentTrades';
import { useChartAutoZoom } from '../hooks/useChartAutoZoom';

import { useEventAlerts } from '../api/useAlerts';
import type { AlertsOverlayData } from '../api/types';

import { useInteractionState } from '../hooks/useInteractionState';
import { SignalInteractionContext } from '../context/SignalInteractionContext';
import { EventSubscribeBell } from '../../../notifications/EventSubscribeBell';
import { CONTENT_HEIGHT_SX } from '../../layout/chrome';
import { openInNewTab } from '../../../utils/newTab';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ALL_CONVICTION_GROUPS = ['insider', 'winner', 'all_traders'] as const;
type ConvictionGroup = (typeof ALL_CONVICTION_GROUPS)[number];

const GROUP_LABELS: Record<ConvictionGroup, string> = {
    insider: 'Insider',
    winner: 'Winner',
    all_traders: 'All',
};

const CONVICTION_GROUP_COLORS: Record<ConvictionGroup, string> = {
    insider: '#f59e0b',
    winner: '#10b981',
    all_traders: '#888888',
};

// Palette + helpers for groups discovered at runtime (active-groups endpoint)
// that aren't in the static maps above.
const CONVICTION_FALLBACK_COLORS = [
    '#6366f1',
    '#ec4899',
    '#14b8a6',
    '#f97316',
    '#a855f7',
    '#0ea5e9',
];

function convictionGroupColor(group: string): string {
    if (group in CONVICTION_GROUP_COLORS) {
        return CONVICTION_GROUP_COLORS[group as ConvictionGroup];
    }
    let h = 0;
    for (let i = 0; i < group.length; i++)
        h = (h * 31 + group.charCodeAt(i)) >>> 0;
    return CONVICTION_FALLBACK_COLORS[h % CONVICTION_FALLBACK_COLORS.length];
}

function convictionGroupLabel(group: string): string {
    if (group in GROUP_LABELS) return GROUP_LABELS[group as ConvictionGroup];
    return group.charAt(0).toUpperCase() + group.slice(1).replace(/_/g, ' ');
}

// A group's runner-up pick gets a second circle only when it's both absolutely
// meaningful and close enough to its top pick.
const CONVICTION_SECOND_PICK_MIN = 0.15;
const CONVICTION_SECOND_PICK_RATIO = 0.6;

// Minimum net capital (capital_yes − capital_no) for a conviction circle to show.
// Scaled up on high-volume markets so a small position on a huge market is hidden.
const CONVICTION_MIN_NET_CAPITAL = 1_000;
const CONVICTION_MIN_NET_CAPITAL_1M = 5_000; // market volume > $1M
const CONVICTION_MIN_NET_CAPITAL_10M = 10_000; // market volume > $10M

function convictionMinNetCapital(volume: number): number {
    if (volume > 10_000_000) return CONVICTION_MIN_NET_CAPITAL_10M;
    if (volume > 1_000_000) return CONVICTION_MIN_NET_CAPITAL_1M;
    return CONVICTION_MIN_NET_CAPITAL;
}

const UNKNOWN_RANGE_RESOLUTION = 60;

const NOW_BUCKET_MS = 5 * 60 * 1000;

function bucketedNow(): number {
    return Math.ceil(Date.now() / NOW_BUCKET_MS) * NOW_BUCKET_MS;
}

const CONVICTION_METRIC_OPTIONS: { value: ConvictionMetric; label: string }[] =
    [
        { value: 'net_capital', label: 'Net Capital' },
        { value: 'net_traders', label: 'Net Traders' },
        { value: 'raw_implied', label: 'Implied' },
        { value: 'open_shares_net', label: 'Open Shares' },
    ];

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function displayLabel(
    market: EventMarketDetail,
    isMultiMarket: boolean
): string {
    if (isMultiMarket && market.yes_outcome) return market.yes_outcome;
    return market.question;
}

// ---------------------------------------------------------------------------
// EventView
// ---------------------------------------------------------------------------

export function EventView() {
    // --- Routing ---
    const { eventId: eventParam } = useParams<{ eventId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamsRef = useRef(searchParams);
    searchParamsRef.current = searchParams;
    const navigate = useNavigate();
    const theme = useTheme();
    const expandedRef = useRef<HTMLDivElement>(null);

    // --- Remote data: event ---
    const {
        data: eventData,
        loading,
        error,
    } = useEventMarkets(eventParam ?? null);

    // Rewrite URL from event_id → slug once data loads
    useEffect(() => {
        if (!eventData?.slug || !eventParam) return;
        const loadedId = eventData.event_id;
        const isDataForCurrentParam =
            eventParam === loadedId || eventParam === eventData.slug;
        if (isDataForCurrentParam && eventParam !== eventData.slug) {
            navigate(
                `/markets/event/${eventData.slug}${window.location.search}`,
                {
                    replace: true,
                }
            );
        }
    }, [eventData?.slug, eventData?.event_id, eventParam, navigate]);

    const markets = eventData?.markets || [];
    const isMultiMarket = markets.length > 1;
    const isSingleMarket = markets.length === 1;
    const eventImage = eventData?.event_image ?? null;

    const [marketSort, setMarketSort] = useEventMarketSort();

    // The active sort key, without the closed-below-live rank. Drives both the
    // fold and the per-list ordering so they never disagree.
    const innerComparator = useMemo(
        () => (marketSort === 'probability' ? byProbabilityDesc : byVolumeDesc),
        [marketSort]
    );

    // --- Default visible / hidden market split ---
    // The sort key decides membership: probability mode promotes the highest
    // -probability markets above the fold (and into the default chart).
    const { shownMarkets, hiddenMarkets } = useMemo(
        () => splitMarkets(markets, innerComparator),
        [markets, innerComparator]
    );

    // --- Market selection (URL-synced) ---
    const {
        selectedMarketIds,
        effectiveSelectedIds,
        hasSelection,
        updateSelectedMarkets,
        handleToggleSelect: baseHandleToggleSelect,
    } = useMarketSelection({ markets, isSingleMarket });

    // Scroll into view after toggling
    const handleToggleSelect = useCallback(
        (conditionId: string) => {
            baseHandleToggleSelect(conditionId);
            setTimeout(() => {
                expandedRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }, 100);
        },
        [baseHandleToggleSelect]
    );

    // --- Chart / time state ---
    const [resolution, setResolution] = useState<CandleResolution | null>(null);
    const [, setChartMode] = useState<'line' | 'candles'>('line');
    const [timeRange, setTimeRange] = useState<[Date, Date] | null>(() => {
        const fromParam = searchParams.get('from');
        const toParam = searchParams.get('to');
        if (fromParam && toParam) {
            const from = new Date(fromParam);
            const to = new Date(toParam);
            if (!isNaN(from.getTime()) && !isNaN(to.getTime()) && from < to)
                return [from, to];
        }
        return null;
    });
    const userSetResolution = useRef(false);
    const userSetTimeRange = useRef(false);

    // Reset chart zoom on event navigation
    const isFirstMount = useRef(true);
    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }
        setTimeRange(null);
        setResolution(null);
        userSetTimeRange.current = false;
        userSetResolution.current = false;
    }, [eventParam]);

    // Reset auto-zoom state when user clears time range
    const prevUserSetTimeRange = useRef(false);
    useEffect(() => {
        if (prevUserSetTimeRange.current && !timeRange) {
            userSetTimeRange.current = false;
        }
        prevUserSetTimeRange.current = !!timeRange;
    }, [timeRange]);

    // Sync timeRange → URL
    useEffect(() => {
        const current = searchParamsRef.current;
        const newFrom = timeRange ? timeRange[0].toISOString() : null;
        const newTo = timeRange ? timeRange[1].toISOString() : null;
        if (current.get('from') !== newFrom || current.get('to') !== newTo) {
            const params = new URLSearchParams(current);
            if (timeRange) {
                params.set('from', newFrom!);
                params.set('to', newTo!);
            } else {
                params.delete('from');
                params.delete('to');
            }
            setSearchParams(params, { replace: true });
        }
    }, [timeRange, setSearchParams]);

    const handleResolutionChange = useCallback((res: CandleResolution) => {
        userSetResolution.current = true;
        setResolution(res);
    }, []);

    const handleTimeRangeChange = useCallback((range: [Date, Date] | null) => {
        userSetTimeRange.current = true;
        setTimeRange(range);
        if (!range) {
            userSetResolution.current = false;
            setResolution(null);
        }
    }, []);

    // --- Market ID sets for streaming / fetching ---
    // Open follows each market's closed flag, not end_date: Polymarket's end_date
    // is an estimate and markets keep trading past it until they close.
    const openMarketIds = useMemo(
        () => markets.filter(m => !m.closed).map(m => m.condition_id),
        [markets]
    );
    const streamMarketIds = useMemo(
        () =>
            effectiveSelectedIds.size > 0
                ? openMarketIds.filter(id => effectiveSelectedIds.has(id))
                : openMarketIds,
        [openMarketIds, effectiveSelectedIds]
    );

    // --- Live trades stream ---
    const {
        liveTrades,
        connected: liveConnected,
        liveProbs,
        liveVolumes,
        liveTraderDeltasByMarket,
    } = useLiveTrades({ streamMarketIds, effectiveSelectedIds });

    const eventHasOpenMarket = openMarketIds.length > 0;
    const dataEndDate = tradesEndDate(eventData?.end_date, eventHasOpenMarket);

    // --- Historical trades ---
    // Scoped to the chart's window when one is set, so paging walks back inside
    // the zoom rather than from the event's beginning.
    const tradesFrom = timeRange
        ? timeRange[0].toISOString()
        : eventData?.start_date;
    const tradesTo = timeRange ? timeRange[1].toISOString() : dataEndDate;

    const {
        trades: historicalTrades,
        loading: loadingHistorical,
        hasMore: hasMoreHistorical,
        error: historicalError,
        loadedForWindow: tradesLoadedForWindow,
        loadPage: loadMoreTrades,
    } = useEventTrades(eventData?.event_id ?? null, tradesFrom, tradesTo);

    useEffect(() => {
        if (eventData?.event_id) loadMoreTrades();
    }, [eventData?.event_id, loadMoreTrades]);

    // --- Color maps ---
    // Assign colors based on ALL markets so colors stay stable when selection changes.
    const colorMap = useMemo(() => {
        const probSorted = [...shownMarkets].sort(
            (a, b) => (b.current_yes_prob ?? 0) - (a.current_yes_prob ?? 0)
        );
        return assignMarketColors(probSorted.map(m => m.condition_id));
    }, [shownMarkets]);

    const sidebarOutcomeColorMap = useMemo(
        () => (isMultiMarket ? colorMap : undefined),
        [isMultiMarket, colorMap]
    );

    // --- Derived market lists for rendering ---
    // Resolved markets stay below live ones, as splitMarkets already ranks them.
    const marketComparator = useMemo(
        () => (a: EventMarketDetail, b: EventMarketDetail) =>
            closedRank(a) - closedRank(b) || innerComparator(a, b),
        [innerComparator]
    );

    const sortedShownMarkets = useMemo(() => {
        const base = hasSelection
            ? [...markets].filter(m => effectiveSelectedIds.has(m.condition_id))
            : shownMarkets;
        return [...base].sort(marketComparator);
    }, [
        shownMarkets,
        hasSelection,
        effectiveSelectedIds,
        markets,
        marketComparator,
    ]);

    const sortedAdditionalMarkets = useMemo(() => {
        const base = hasSelection
            ? [...markets].filter(
                m => !effectiveSelectedIds.has(m.condition_id)
            )
            : hiddenMarkets;
        return [...base].sort(marketComparator);
    }, [
        hiddenMarkets,
        hasSelection,
        effectiveSelectedIds,
        markets,
        marketComparator,
    ]);

    const chartMarkets = useMemo(() => {
        if (hasSelection)
            return markets.filter(m =>
                effectiveSelectedIds.has(m.condition_id)
            );
        return shownMarkets;
    }, [hasSelection, effectiveSelectedIds, shownMarkets, markets]);

    // conditionId of the one selected market (for NO-line + conviction chart scoping)
    const tradeMarketId =
        effectiveSelectedIds.size === 1 ? [...effectiveSelectedIds][0] : null;

    const noLineConditionId = tradeMarketId;

    const marketMeta: MarketLineMeta[] = useMemo(
        () =>
            [...chartMarkets]
                .sort(
                    (a, b) =>
                        (b.current_yes_prob ?? 0) - (a.current_yes_prob ?? 0)
                )
                .map(m => ({
                    conditionId: m.condition_id,
                    question:
                        isMultiMarket && m.yes_outcome
                            ? m.yes_outcome
                            : m.question,
                    outcomes: m.outcomes,
                    color:
                        colorMap.get(m.condition_id) ??
                        theme.palette.primary.main,
                })),
        [chartMarkets, colorMap, theme, isMultiMarket]
    );

    // --- Remote data: probability history ---
    const probConditionIds = useMemo(
        () => chartMarkets.map(m => m.condition_id),
        [chartMarkets]
    );
    const probStartDate = useMemo(
        () => (eventData?.start_date ? new Date(eventData.start_date) : null),
        [eventData?.start_date]
    );
    const [nowMs, setNowMs] = useState(bucketedNow);
    useEffect(() => {
        // Tick just after each bucket boundary, so nowMs (rounded up) never
        // falls behind the real now; an unaligned interval could lag a bucket.
        const tick = () => setNowMs(bucketedNow());
        let interval: ReturnType<typeof setInterval> | undefined;
        const timeout = setTimeout(
            () => {
                tick();
                interval = setInterval(tick, NOW_BUCKET_MS);
            },
            bucketedNow() - Date.now() + 1
        );
        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
        };
    }, []);

    const eventEndDate = useMemo(
        () => (eventData?.end_date ? new Date(eventData.end_date) : null),
        [eventData?.end_date]
    );
    const probEndDate = useMemo(
        () => chartEndDate(eventEndDate, eventHasOpenMarket, nowMs),
        [eventEndDate, eventHasOpenMarket, nowMs]
    );

    // Windowed fetch: only load the visible (zoomed) range, falling back to the
    // full event when not zoomed. This lets fine candle lengths (1m/5m) become
    // available as you zoom in — the candle-budget safeguard is applied against
    // the window actually being fetched, so it still can't request a ruinous
    // number of candles over a long span.
    const [dataStartMs, setDataStartMs] = useState<number | null>(null);
    const marketsKey = probConditionIds.join(',');
    useEffect(() => {
        setDataStartMs(null);
    }, [marketsKey]);

    const fetchStart = useMemo(() => {
        if (timeRange) return timeRange[0];
        if (dataStartMs === null) return probStartDate;
        if (!probStartDate) return new Date(dataStartMs);
        return dataStartMs > probStartDate.getTime()
            ? new Date(dataStartMs)
            : probStartDate;
    }, [timeRange, dataStartMs, probStartDate]);

    const fetchEnd = useMemo(() => {
        if (timeRange) return timeRange[1];
        if (!probEndDate) return probEndDate;
        return probEndDate.getTime() > nowMs ? new Date(nowMs) : probEndDate;
    }, [timeRange, probEndDate, nowMs]);
    const effectiveResolution = useMemo(() => {
        const options = RESOLUTION_OPTIONS.map(o => o.value);
        const fallback = resolution ?? UNKNOWN_RANGE_RESOLUTION;
        if (!fetchStart || !fetchEnd) return fallback;
        const rangeMinutes =
            (fetchEnd.getTime() - fetchStart.getTime()) / 60000;
        const budget = candleBudgetForSeries(probConditionIds.length);
        if (resolution === null)
            return bestResolutionForBudget(rangeMinutes, options, budget);
        return clampResolutionToBudget(
            rangeMinutes,
            resolution,
            options,
            budget
        );
    }, [fetchStart, fetchEnd, resolution, probConditionIds.length]);

    const { series: probSeries, loading: probLoading } =
        useMultiMarketProbability(
            probConditionIds,
            fetchStart,
            fetchEnd,
            effectiveResolution
        );

    // The NO side of every market on the chart, so each market's volume bar is
    // its real taker volume rather than the half of it that traded YES. The
    // chart shows at most MAX_DEFAULT_SHOWN_ACTIVE markets, so this is a handful
    // of extra requests, not one per market in the event.
    const noSeriesByMarket = useMarketsOutcomeSeries(
        probConditionIds,
        fetchStart,
        fetchEnd,
        effectiveResolution,
        1
    );

    useEffect(() => {
        if (probLoading) return;
        let first: number | null = null;
        for (const s of probSeries) {
            const t = s.data[0]?.t;
            if (t === undefined) continue;
            const ms = new Date(t).getTime();
            if (first === null || ms < first) first = ms;
        }
        if (first === null) return;
        const firstMs = first;
        setDataStartMs(prev =>
            prev === null || firstMs < prev ? firstMs : prev
        );
    }, [probSeries, probLoading]);

    // --- Chart auto-zoom ---
    useChartAutoZoom({
        tradeMarketId,
        probSeries,
        probLoading,
        probStartDate,
        probEndDate,
        userSetTimeRange,
        userSetResolution,
        setTimeRange,
        setResolution,
    });

    // --- Volume calculations ---
    const apiTotalVolume = eventData?.total_volume ?? 0;

    const candleVolume = useMemo(() => {
        const map = new Map<string, number>();
        for (const s of probSeries) {
            const vol = s.data.reduce((sum, d) => sum + d.vol, 0);
            if (vol > 0) map.set(s.conditionId, vol);
        }
        return map;
    }, [probSeries]);

    const getMarketVolume = useCallback(
        (conditionId: string, apiVolume: number | null): number => {
            if (apiVolume && apiVolume > 0) return apiVolume;
            return candleVolume.get(conditionId) ?? 0;
        },
        [candleVolume]
    );

    const totalVolume = useMemo(() => {
        if (apiTotalVolume > 0) return apiTotalVolume;
        return [...(eventData?.markets || [])].reduce(
            (acc, m) => acc + getMarketVolume(m.condition_id, m.volume),
            0
        );
    }, [apiTotalVolume, eventData?.markets, getMarketVolume]);

    // --- Asset price overlay ---
    const effectiveStartDate = timeRange?.[0] ?? probStartDate;
    const utcDayStartMs = startOfUtcDay(nowMs);
    const assetEndDate = useMemo(
        () =>
            assetOverlayEndDate(eventEndDate, eventHasOpenMarket, utcDayStartMs),
        [eventEndDate, eventHasOpenMarket, utcDayStartMs]
    );
    const effectiveEndDate = timeRange?.[1] ?? assetEndDate;

    const [selectedAssets, setSelectedAssets] = useState<SelectedAsset[]>(
        () => {
            const assetTicker = searchParams.get('asset');
            const assetType = searchParams.get('assetType');
            const assetName = searchParams.get('assetName');
            if (assetTicker && assetType) {
                return [
                    {
                        ticker: assetTicker,
                        assetType,
                        name: assetName || assetTicker,
                    },
                ];
            }
            return [];
        }
    );

    // Clean up asset params from URL
    useEffect(() => {
        if (searchParams.has('asset')) {
            const params = new URLSearchParams(searchParams);
            params.delete('asset');
            params.delete('assetType');
            params.delete('assetName');
            setSearchParams(params, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleAddAsset = useCallback((asset: SelectedAsset) => {
        setSelectedAssets(prev => {
            if (prev.length >= 2 || prev.some(a => a.ticker === asset.ticker))
                return prev;
            return [...prev, asset];
        });
    }, []);

    const handleRemoveAsset = useCallback((ticker: string) => {
        setSelectedAssets(prev => prev.filter(a => a.ticker !== ticker));
    }, []);

    const assetOverlays = useAssetPriceOverlay(
        selectedAssets,
        effectiveStartDate,
        effectiveEndDate,
        effectiveResolution
    );

    // --- Trader overlay ---
    const {
        highlightedAccounts,
        accountColorMap,
        loadingAccounts,
        tradesByAccount,
        mergedTradesByAccount,
        tradeOverlay,
        handleToggleAccount,
    } = useTraderOverlay({
        tradeMarketId,
        liveTrades,
        eventParam,
        eventId: eventData?.event_id ?? null,
        eventStartDate: eventData?.start_date,
        eventEndDate: dataEndDate,
    });

    // Recent trades for the selected traders across the whole event, merged into the
    // trades sidebar so a dormant trader's freshest trades appear in Accounts mode even
    // when they fall outside the loaded event-wide window. Driven by the selection state.
    // Shares the chart's window: this hook returns only the 50 newest per account, and
    // unscoped those 50 can all post-date a historical window, hiding the trader entirely.
    const {
        trades: selectedTraderTrades,
        loading: selectedTraderTradesLoading,
    } = useSelectedTraderRecentTrades(
        eventData?.event_id ?? null,
        tradesFrom,
        tradesTo,
        highlightedAccounts
    );

    // --- Signals ---
    const selectedMarketIdsArray = useMemo(
        () => (hasSelection ? [...effectiveSelectedIds] : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [hasSelection, [...effectiveSelectedIds].sort().join(',')]
    );

    const {
        alerts,
        loading: alertsLoading,
        error: alertsError,
    } = useEventAlerts(eventData?.event_id ?? null);
    const alertsOverlay: AlertsOverlayData = useMemo(
        () => ({ alerts }),
        [alerts]
    );

    // Single selected market for signal scoping
    const selectedMarketForSignals = tradeMarketId;

    // Sidebar: scope to single selected market, or show event-wide
    const { signals: activeSignals, loading: activeSignalsLoading } =
        useMarketSignals(tradeMarketId);

    // marketSignalsByMarket — market-level signals for the chart overlay
    const marketSignalsByMarket = useMemo((): Map<string, SignalItem[]> => {
        if (!tradeMarketId || activeSignals.length === 0) return new Map();
        const filtered = activeSignals.filter(sig =>
            MARKET_SIGNAL_TYPES.has(sig.signal_type)
        );
        return new Map([[tradeMarketId, filtered]]);
    }, [tradeMarketId, activeSignals]);

    const signalOverlay = useMemo(
        (): SignalOverlayData => ({
            byMarket: marketSignalsByMarket,
            colorMap,
        }),
        [marketSignalsByMarket, colorMap]
    );

    // --- Signal highlight ---
    const [signalInteraction, signalActions] = useInteractionState<string>();

    // When a signal is selected (from sidebar or chart), also select its market
    // so the chart has the right data loaded.
    const prevSelectedSignalId = useRef<string | null>(null);
    useEffect(() => {
        const id = signalInteraction.selectedId;
        if (id === prevSelectedSignalId.current) return;
        prevSelectedSignalId.current = id;
        if (!id) return;
        // Find the market this signal belongs to and select it
        const sig = activeSignals.find(s => s.id === id);
        if (sig) updateSelectedMarkets(new Set([sig.market_id]));
    }, [signalInteraction.selectedId, activeSignals, updateSelectedMarkets]);

    // Clear selection when all markets are deselected
    useEffect(() => {
        if (effectiveSelectedIds.size === 0) signalActions.select(null);
    }, [effectiveSelectedIds.size, signalActions]);

    // --- Conviction timeseries ---
    const [convictionRawMode, setConvictionRawMode] = useState(false);
    const [selectedConvictionGroup, setSelectedConvictionGroup] =
        useState<ConvictionGroup>('insider');
    const [convictionMetric, setConvictionMetric] =
        useState<ConvictionMetric>('net_capital');
    const [convAnchor, setConvAnchor] = useState<HTMLElement | null>(null);

    const convictionResolution: ConvictionResolution = convictionRawMode
        ? 'raw'
        : effectiveResolution <= 1
            ? '1m'
            : effectiveResolution <= 5
                ? '5m'
                : effectiveResolution <= 15
                    ? '15m'
                    : '1h';

    const { byGroup: convictionByGroup, loading: convictionLoading } =
        useConvictionTimeseries(tradeMarketId, convictionResolution, [
            ...ALL_CONVICTION_GROUPS,
        ]);

    const filteredConvictionByGroup = useMemo((): Map<
        string,
        ConvictionSeries
    > => {
        const s = convictionByGroup.get(selectedConvictionGroup);
        if (!s || s.points.length === 0) return new Map();
        return new Map([[selectedConvictionGroup, s]]);
    }, [convictionByGroup, selectedConvictionGroup]);

    // Auto-fallback: if the selected group loads with no data, pick the first group that does
    useEffect(() => {
        if (!convictionByGroup.size) return;
        const current = convictionByGroup.get(selectedConvictionGroup);
        if (current && current.points.length > 0) return;
        for (const group of ALL_CONVICTION_GROUPS) {
            const s = convictionByGroup.get(group);
            if (s && s.points.length > 0) {
                setSelectedConvictionGroup(group);
                return;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [convictionByGroup]);

    // --- Latest conviction per shown market (for candidate-row circles) ---
    const shownConvictionMarketIds = useMemo(
        () => sortedShownMarkets.map(m => m.condition_id),
        [sortedShownMarkets]
    );

    // One batch call returns the latest point per (market, group) and the set
    // of groups active for these markets; circles are driven by that set,
    // falling back to the static list while empty/loading.
    const { byMarket: latestConvictionByMarket, activeGroups } =
        useLatestConvictionByMarket(shownConvictionMarketIds, []);
    const circleGroups = useMemo(
        () =>
            activeGroups.length > 0 ? activeGroups : [...ALL_CONVICTION_GROUPS],
        [activeGroups]
    );

    // Resolved volume per shown market — matches the figure shown in each row,
    // used to scale the conviction net-capital floor.
    const marketVolumeById = useMemo(() => {
        const m = new Map<string, number>();
        for (const mk of sortedShownMarkets) {
            m.set(mk.condition_id, getMarketVolume(mk.condition_id, mk.volume));
        }
        return m;
    }, [sortedShownMarkets, getMarketVolume]);

    const convictionCirclesByMarket = useMemo(() => {
        const result = new Map<string, RowConvictionCircle[]>();
        if (latestConvictionByMarket.size === 0) return result;

        // Only a decisively-settled market shows a result glyph; a closed market
        // sitting at an in-between price stays rendered as active.
        const resolvedById = new Map<string, boolean>();
        for (const mk of sortedShownMarkets) {
            if (!mk.closed) continue;
            const p = mk.current_yes_prob;
            if (p == null) continue;
            if (p > 0.99) resolvedById.set(mk.condition_id, true);
            else if (p < 0.01) resolvedById.set(mk.condition_id, false);
        }

        const add = (marketId: string, circle: RowConvictionCircle) => {
            const arr = result.get(marketId);
            if (arr) arr.push(circle);
            else result.set(marketId, [circle]);
        };

        for (const group of circleGroups) {
            const picks: {
                marketId: string;
                rawImplied: number;
                point: ConvictionTimePoint;
            }[] = [];
            for (const [marketId, byGroup] of latestConvictionByMarket) {
                const point = byGroup.get(group);
                if (!point || point.direction === 'NO') continue;
                const floor = convictionMinNetCapital(
                    marketVolumeById.get(marketId) ?? 0
                );
                if (point.net_capital < floor) continue;
                picks.push({
                    marketId,
                    rawImplied: point.raw_implied,
                    point,
                });
            }
            if (picks.length === 0) continue;
            picks.sort((a, b) => b.rawImplied - a.rawImplied);

            const top = picks[0];
            add(top.marketId, {
                group,
                label: convictionGroupLabel(group),
                color: convictionGroupColor(group),
                rawImplied: top.rawImplied,
                rank: 'primary',
                point: top.point,
                resolved: resolvedById.has(top.marketId),
                correct: resolvedById.get(top.marketId),
            });

            const second = picks[1];
            if (
                second &&
                second.rawImplied >= CONVICTION_SECOND_PICK_MIN &&
                second.rawImplied >=
                CONVICTION_SECOND_PICK_RATIO * top.rawImplied
            ) {
                add(second.marketId, {
                    group,
                    label: convictionGroupLabel(group),
                    color: convictionGroupColor(group),
                    rawImplied: second.rawImplied,
                    rank: 'secondary',
                    point: second.point,
                    resolved: resolvedById.has(second.marketId),
                    correct: resolvedById.get(second.marketId),
                });
            }
        }

        // Largest net capital first (left) → smallest (right).
        for (const arr of result.values()) {
            arr.sort((a, b) => b.point.net_capital - a.point.net_capital);
        }
        return result;
    }, [
        latestConvictionByMarket,
        circleGroups,
        marketVolumeById,
        sortedShownMarkets,
    ]);

    const traderSignalsForConviction = useMemo((): SignalItem[] => {
        if (!tradeMarketId) return [];
        return activeSignals.filter(
            sig =>
                sig.market_id === tradeMarketId &&
                !MARKET_SIGNAL_TYPES.has(sig.signal_type)
        );
    }, [tradeMarketId, activeSignals]);

    // --- Related events panel state ---
    const [panelHoveredEvent, setPanelHoveredEvent] =
        useState<EventFromAsset | null>(null);
    const handlePanelSelectEvent = useCallback((evt: EventFromAsset) => {
        openInNewTab(`/markets/event/${evt.slug || evt.event_id}`);
    }, []);

    // --- Additional markets toggle ---
    const [showAdditionalMarkets, setShowAdditionalMarkets] = useState(false);
    const [hoveredTraderAddress, setHoveredTraderAddress] = useState<
        string | null
    >(null);
    // Shared hover key linking the trades list and the plot trade markers.
    // Owned here, not in the sidebar: the chart overlay draws the same trades,
    // and two views deriving the window separately would drift apart.
    const tradeSelection = useEventTradeSelection({
        liveTrades,
        historicalTrades,
        selectedTraderTrades,
        timeRange,
        highlightedAccounts,
    });

    const [hoveredTradeKey, setHoveredTradeKey] = useState<string | null>(null);

    // --- Sidebar panels ---
    const [leftPanel, setLeftPanel] = useSidebarPanel('event.left', 'trades');
    const [rightPanel, setRightPanel] = useSidebarPanel(
        'event.right',
        'alerts'
    );

    // Auto-switch right panel to signals when a market is selected
    const prevTradeMarketId = useRef<string | null>(null);
    useEffect(() => {
        if (tradeMarketId && tradeMarketId !== prevTradeMarketId.current) {
            setRightPanel('signals');
        }
        prevTradeMarketId.current = tradeMarketId;
    }, [tradeMarketId, setRightPanel]);

    const handleSelectMarket = useCallback(
        (conditionId: string) => {
            const next = new Set(selectedMarketIds);
            if (next.has(conditionId)) next.delete(conditionId);
            else next.add(conditionId);
            updateSelectedMarkets(next);
        },
        [selectedMarketIds, updateSelectedMarkets]
    );

    const leftPanels = useMemo(
        () => [
            {
                id: 'trades',
                label: 'Trades',
                icon: <ShowChartIcon />,
                content: (
                    <LiveTradesSidebarView
                        liveTrades={liveTrades}
                        markets={markets}
                        liveProbs={liveProbs}
                        connected={liveConnected}
                        historicalTrades={historicalTrades}
                        loadMoreTrades={loadMoreTrades}
                        loadingMore={loadingHistorical}
                        hasMore={hasMoreHistorical}
                        error={historicalError}
                        highlightedAccounts={highlightedAccounts}
                        selectedTraderTrades={selectedTraderTrades}
                        selectedTraderTradesLoading={
                            selectedTraderTradesLoading
                        }
                        tradesByAccount={mergedTradesByAccount}
                        accountColorMap={accountColorMap}
                        outcomeColorMap={sidebarOutcomeColorMap}
                        onSelectMarket={handleSelectMarket}
                        getTraderHref={address => `/markets/trader/${address}`}
                        onToggleAccount={address =>
                            handleToggleAccount(`polymarket:${address}`)
                        }
                        signals={activeSignals}
                        hoveredTradeKey={hoveredTradeKey}
                        onTradeHover={setHoveredTradeKey}
                        timeRange={timeRange}
                        loadedForWindow={tradesLoadedForWindow}
                        tradeSelection={tradeSelection}
                    />
                ),
            },
        ],
        [
            liveTrades,
            markets,
            liveProbs,
            liveConnected,
            historicalTrades,
            loadMoreTrades,
            loadingHistorical,
            hasMoreHistorical,
            historicalError,
            highlightedAccounts,
            selectedTraderTrades,
            selectedTraderTradesLoading,
            mergedTradesByAccount,
            accountColorMap,
            sidebarOutcomeColorMap,
            handleSelectMarket,
            handleToggleAccount,
            activeSignals,
            hoveredTradeKey,
            timeRange,
            tradesLoadedForWindow,
            tradeSelection,
        ]
    );

    const rightPanels = useMemo(
        () => [
            {
                id: 'alerts',
                label: 'Alerts',
                icon: <WarningAmberIcon />,
                content: (
                    <AlertsPanel
                        alerts={alerts}
                        loading={alertsLoading}
                        error={alertsError?.message}
                        markets={markets}
                        outcomeColorMap={sidebarOutcomeColorMap}
                        onSelectMarket={handleSelectMarket}
                    />
                ),
            },
            {
                id: 'signals',
                label: 'Signals',
                icon: <InsightsIcon />,
                disabled: !tradeMarketId, // ← disable instead of omit
                content: (
                    <SignalsPanel
                        signals={activeSignals}
                        loading={activeSignalsLoading}
                        showMarketBadge={false}
                    />
                ),
            },
        ],
        [
            tradeMarketId,
            activeSignals,
            activeSignalsLoading,
            alerts,
            alertsLoading,
            alertsError,
            markets,
            sidebarOutcomeColorMap,
            handleSelectMarket,
        ]
    );

    if (!eventParam) return null;

    return (
        <SignalInteractionContext.Provider
            value={{ state: signalInteraction, actions: signalActions }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        lg: '320px minmax(0px, 1320px) 320px',
                    },
                    maxWidth: 1960,
                    mx: 'auto',
                    ...CONTENT_HEIGHT_SX,
                    overflow: 'hidden',
                }}
            >
                {/* Left sidebar */}
                <Box
                    sx={{
                        display: { xs: 'none', lg: 'block' },
                        height: '100%',
                        overflowY: 'hidden',
                        overscrollBehavior: 'contain',
                        borderRight: 1,
                        borderColor: 'divider',
                    }}
                >
                    <SidebarShell
                        panels={leftPanels}
                        activePanel={leftPanel}
                        onPanelChange={setLeftPanel}
                    />
                </Box>

                {/* Main content */}
                <Box
                    sx={{
                        px: 2,
                        pb: 10,
                        minWidth: 0,
                        height: '100%',
                        ...hoverScrollbar,
                        overscrollBehavior: 'contain',
                    }}
                >
                    <EventHeader
                        title={eventData?.event_title}
                        image={eventImage}
                        marketCount={markets.length}
                        totalVolume={totalVolume}
                        tags={eventData?.tags || []}
                        endDate={eventData?.end_date}
                        isOpen={markets.length === 0 || openMarketIds.length > 0}
                        loading={loading}
                        slug={eventData?.slug}
                        description={eventData?.description}
                        liveConnected={liveConnected}
                        eventId={eventData?.event_id}
                    />

                    {!loading && chartMarkets.length > 0 && (
                        <ConvergenceChart
                            series={probSeries}
                            marketMeta={marketMeta}
                            mutuallyExclusive={eventData?.neg_risk === true}
                            loading={probLoading}
                            highlightedMarkets={null}
                            onMarketClick={handleToggleSelect}
                            resolution={effectiveResolution}
                            onResolutionChange={handleResolutionChange}
                            onChartTypeChange={setChartMode}
                            timeRange={timeRange}
                            onTimeRangeChange={handleTimeRangeChange}
                            eventStartDate={
                                eventData?.start_date
                                    ? new Date(eventData.start_date)
                                    : null
                            }
                            eventEndDate={probEndDate}
                            assetOverlays={assetOverlays}
                            noSeriesByMarket={noSeriesByMarket}
                            tradeOverlay={tradeOverlay}
                            windowTrades={tradeSelection.filteredTrades}
                            signalOverlay={signalOverlay}
                            alertsOverlay={alertsOverlay}
                            showNoLine={noLineConditionId}
                            hoveredTraderAddress={hoveredTraderAddress}
                            hoveredTradeKey={hoveredTradeKey}
                            onTradeHover={setHoveredTradeKey}
                            convictionByGroup={
                                tradeMarketId
                                    ? filteredConvictionByGroup
                                    : undefined
                            }
                            convictionLoading={convictionLoading}
                            traderSignals={traderSignalsForConviction}
                            onTraderHover={setHoveredTraderAddress}
                            convictionMetric={convictionMetric}
                            convictionControls={
                                tradeMarketId
                                    ? (() => {
                                        const triggerLabel = `${GROUP_LABELS[selectedConvictionGroup]} · ${CONVICTION_METRIC_OPTIONS.find(o => o.value === convictionMetric)?.label ?? ''}`;
                                        return (
                                            <>
                                                <Box
                                                    component="button"
                                                    onClick={e =>
                                                        setConvAnchor(
                                                            e.currentTarget as HTMLElement
                                                        )
                                                    }
                                                    sx={{
                                                        height: 26,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        px: 1,
                                                        border: '1px solid',
                                                        borderColor:
                                                            convAnchor
                                                                ? 'text.secondary'
                                                                : 'divider',
                                                        borderRadius: '4px',
                                                        bgcolor:
                                                            'transparent',
                                                        cursor: 'pointer',
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        fontFamily: 'inherit',
                                                        color: 'text.primary',
                                                        whiteSpace: 'nowrap',
                                                        '&:hover': {
                                                            borderColor:
                                                                'text.secondary',
                                                        },
                                                    }}
                                                >
                                                    {triggerLabel}
                                                    <ExpandMoreIcon
                                                        sx={{
                                                            fontSize: 14,
                                                            opacity: 0.5,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                </Box>
                                                <Popover
                                                    open={Boolean(convAnchor)}
                                                    anchorEl={convAnchor}
                                                    onClose={() =>
                                                        setConvAnchor(null)
                                                    }
                                                    anchorOrigin={{
                                                        vertical: 'bottom',
                                                        horizontal: 'left',
                                                    }}
                                                    transformOrigin={{
                                                        vertical: 'top',
                                                        horizontal: 'left',
                                                    }}
                                                    slotProps={{
                                                        paper: {
                                                            sx: { mt: 0.5 },
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                        }}
                                                    >
                                                        {/* Group column */}
                                                        <Box
                                                            sx={{
                                                                p: 1,
                                                                borderRight: 1,
                                                                borderColor:
                                                                    'divider',
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize:
                                                                        '9px',
                                                                    fontWeight: 700,
                                                                    letterSpacing:
                                                                        '0.06em',
                                                                    color: 'text.disabled',
                                                                    mb: 0.5,
                                                                    px: 0.75,
                                                                }}
                                                            >
                                                                GROUP
                                                            </Typography>
                                                            {ALL_CONVICTION_GROUPS.map(
                                                                group => {
                                                                    const hasData =
                                                                        (convictionByGroup.get(
                                                                            group
                                                                        )
                                                                            ?.points
                                                                            .length ??
                                                                            0) >
                                                                        0;
                                                                    const isSelected =
                                                                        selectedConvictionGroup ===
                                                                        group;
                                                                    const dotColor =
                                                                        CONVICTION_GROUP_COLORS[
                                                                        group
                                                                        ];
                                                                    return (
                                                                        <Box
                                                                            key={
                                                                                group
                                                                            }
                                                                            onClick={() => {
                                                                                if (
                                                                                    hasData
                                                                                ) {
                                                                                    setSelectedConvictionGroup(
                                                                                        group
                                                                                    );
                                                                                    setConvAnchor(
                                                                                        null
                                                                                    );
                                                                                }
                                                                            }}
                                                                            sx={{
                                                                                display:
                                                                                    'flex',
                                                                                alignItems:
                                                                                    'center',
                                                                                gap: 1,
                                                                                px: 0.75,
                                                                                py: 0.4,
                                                                                borderRadius: 0.75,
                                                                                cursor: hasData
                                                                                    ? 'pointer'
                                                                                    : 'default',
                                                                                opacity:
                                                                                    hasData
                                                                                        ? 1
                                                                                        : 0.3,
                                                                                bgcolor:
                                                                                    isSelected
                                                                                        ? alpha(
                                                                                            dotColor,
                                                                                            0.1
                                                                                        )
                                                                                        : 'transparent',
                                                                                '&:hover':
                                                                                    hasData &&
                                                                                        !isSelected
                                                                                        ? {
                                                                                            bgcolor:
                                                                                                'action.hover',
                                                                                        }
                                                                                        : {},
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    width: 8,
                                                                                    height: 8,
                                                                                    borderRadius:
                                                                                        '50%',
                                                                                    flexShrink: 0,
                                                                                    bgcolor:
                                                                                        isSelected
                                                                                            ? dotColor
                                                                                            : 'transparent',
                                                                                    border: '1.5px solid',
                                                                                    borderColor:
                                                                                        isSelected
                                                                                            ? dotColor
                                                                                            : 'text.disabled',
                                                                                    transition:
                                                                                        'all 0.1s',
                                                                                }}
                                                                            />
                                                                            <Typography
                                                                                sx={{
                                                                                    fontSize:
                                                                                        '12px',
                                                                                    fontWeight:
                                                                                        isSelected
                                                                                            ? 600
                                                                                            : 400,
                                                                                }}
                                                                            >
                                                                                {
                                                                                    GROUP_LABELS[
                                                                                    group
                                                                                    ]
                                                                                }
                                                                            </Typography>
                                                                        </Box>
                                                                    );
                                                                }
                                                            )}
                                                        </Box>

                                                        {/* Metric column */}
                                                        <Box sx={{ p: 1 }}>
                                                            <Typography
                                                                sx={{
                                                                    fontSize:
                                                                        '9px',
                                                                    fontWeight: 700,
                                                                    letterSpacing:
                                                                        '0.06em',
                                                                    color: 'text.disabled',
                                                                    mb: 0.5,
                                                                    px: 0.75,
                                                                }}
                                                            >
                                                                METRIC
                                                            </Typography>
                                                            {CONVICTION_METRIC_OPTIONS.map(
                                                                opt => {
                                                                    const isSelected =
                                                                        convictionMetric ===
                                                                        opt.value;
                                                                    return (
                                                                        <Box
                                                                            key={
                                                                                opt.value
                                                                            }
                                                                            onClick={() => {
                                                                                setConvictionMetric(
                                                                                    opt.value
                                                                                );
                                                                                setConvAnchor(
                                                                                    null
                                                                                );
                                                                            }}
                                                                            sx={{
                                                                                display:
                                                                                    'flex',
                                                                                alignItems:
                                                                                    'center',
                                                                                gap: 1,
                                                                                px: 0.75,
                                                                                py: 0.4,
                                                                                borderRadius: 0.75,
                                                                                cursor: 'pointer',
                                                                                bgcolor:
                                                                                    isSelected
                                                                                        ? 'action.selected'
                                                                                        : 'transparent',
                                                                                '&:hover':
                                                                                    !isSelected
                                                                                        ? {
                                                                                            bgcolor:
                                                                                                'action.hover',
                                                                                        }
                                                                                        : {},
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    width: 8,
                                                                                    height: 8,
                                                                                    borderRadius:
                                                                                        '50%',
                                                                                    flexShrink: 0,
                                                                                    bgcolor:
                                                                                        isSelected
                                                                                            ? 'primary.main'
                                                                                            : 'transparent',
                                                                                    border: '1.5px solid',
                                                                                    borderColor:
                                                                                        isSelected
                                                                                            ? 'primary.main'
                                                                                            : 'text.disabled',
                                                                                    transition:
                                                                                        'all 0.1s',
                                                                                }}
                                                                            />
                                                                            <Typography
                                                                                sx={{
                                                                                    fontSize:
                                                                                        '12px',
                                                                                    fontWeight:
                                                                                        isSelected
                                                                                            ? 600
                                                                                            : 400,
                                                                                    whiteSpace:
                                                                                        'nowrap',
                                                                                }}
                                                                            >
                                                                                {
                                                                                    opt.label
                                                                                }
                                                                            </Typography>
                                                                        </Box>
                                                                    );
                                                                }
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Popover>
                                            </>
                                        );
                                    })()
                                    : undefined
                            }
                            headerExtra={
                                <AssetSearchOverlay
                                    selectedAssets={selectedAssets}
                                    onAdd={handleAddAsset}
                                    onRemove={handleRemoveAsset}
                                />
                            }
                        />
                    )}

                    {/* Market List */}
                    {(() => {
                        const resolvedTimeRange: [Date, Date] | null =
                            effectiveStartDate && effectiveEndDate
                                ? [effectiveStartDate, effectiveEndDate]
                                : timeRange;

                        const renderMarketRow = (
                            market: EventMarketDetail,
                            fallbackColor?: string
                        ) => {
                            const isSelected = effectiveSelectedIds.has(
                                market.condition_id
                            );
                            const color =
                                fallbackColor ??
                                colorMap.get(market.condition_id) ??
                                theme.palette.primary.main;
                            const showImage = isMultiMarket && !!market.image;
                            return (
                                <Box
                                    key={market.condition_id}
                                    ref={isSelected ? expandedRef : undefined}
                                >
                                    <MarketRow
                                        market={market}
                                        label={displayLabel(
                                            market,
                                            isMultiMarket
                                        )}
                                        color={color}
                                        showImage={showImage}
                                        timeRange={resolvedTimeRange}
                                        isSelected={isSelected}
                                        isSingleMarket={isSingleMarket}
                                        isMultiMarket={isMultiMarket}
                                        totalVolume={totalVolume}
                                        onToggle={() =>
                                            handleToggleSelect(
                                                market.condition_id
                                            )
                                        }
                                        highlightedAccounts={
                                            highlightedAccounts
                                        }
                                        onToggleAccount={handleToggleAccount}
                                        loadingAccounts={loadingAccounts}
                                        accountColorMap={accountColorMap}
                                        tradesByAccount={mergedTradesByAccount}
                                        resolvedVolume={getMarketVolume(
                                            market.condition_id,
                                            market.volume
                                        )}
                                        liveProb={liveProbs.get(
                                            market.condition_id
                                        )}
                                        liveVolume={liveVolumes.get(
                                            market.condition_id
                                        )}
                                        liveTraderDeltas={liveTraderDeltasByMarket.get(
                                            market.condition_id
                                        )}
                                        convictionCircles={convictionCirclesByMarket.get(
                                            market.condition_id
                                        )}
                                    />
                                </Box>
                            );
                        };

                        return (
                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    sx={{
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        letterSpacing: '0.04em',
                                        textTransform: 'uppercase',
                                        color: 'text.secondary',
                                        mb: 1,
                                        px: 0.5,
                                    }}
                                >
                                    Markets ({sortedShownMarkets.length}
                                    {sortedAdditionalMarkets.length > 0
                                        ? ` + ${sortedAdditionalMarkets.length} additional`
                                        : ''}
                                    )
                                    {effectiveSelectedIds.size > 0 &&
                                        !isSingleMarket && (
                                            <Box
                                                component="span"
                                                onClick={() =>
                                                    updateSelectedMarkets(
                                                        new Set()
                                                    )
                                                }
                                                sx={{
                                                    ml: 1,
                                                    color: 'primary.main',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        textDecoration:
                                                            'underline',
                                                    },
                                                }}
                                            >
                                                Clear selection (
                                                {effectiveSelectedIds.size})
                                            </Box>
                                        )}
                                    {!loading && markets.length > 1 && (
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 1,
                                                color: 'text.disabled',
                                            }}
                                        >
                                            sort:{' '}
                                            {(
                                                [
                                                    ['volume', 'Volume'],
                                                    [
                                                        'probability',
                                                        'Probability',
                                                    ],
                                                ] as const
                                            ).map(([key, label], i) => (
                                                <Fragment key={key}>
                                                    {i > 0 && ' / '}
                                                    <Box
                                                        component="span"
                                                        role="button"
                                                        tabIndex={0}
                                                        aria-pressed={
                                                            marketSort === key
                                                        }
                                                        onClick={() =>
                                                            setMarketSort(key)
                                                        }
                                                        onKeyDown={e => {
                                                            if (
                                                                e.key ===
                                                                'Enter' ||
                                                                e.key === ' '
                                                            ) {
                                                                e.preventDefault();
                                                                setMarketSort(
                                                                    key
                                                                );
                                                            }
                                                        }}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            color:
                                                                marketSort ===
                                                                    key
                                                                    ? 'primary.main'
                                                                    : 'text.disabled',
                                                            '&:hover': {
                                                                textDecoration:
                                                                    'underline',
                                                            },
                                                        }}
                                                    >
                                                        {label}
                                                    </Box>
                                                </Fragment>
                                            ))}
                                        </Box>
                                    )}
                                </Typography>

                                {loading ? (
                                    Array.from({ length: 3 }).map((_, i) => (
                                        <Skeleton
                                            key={i}
                                            variant="rectangular"
                                            height={72}
                                            sx={{ borderRadius: 1, mb: 1 }}
                                        />
                                    ))
                                ) : error ? (
                                    <Typography
                                        sx={{
                                            color: 'error.main',
                                            py: 3,
                                            textAlign: 'center',
                                            fontSize: '13px',
                                        }}
                                    >
                                        Failed to load event data
                                    </Typography>
                                ) : (
                                    sortedShownMarkets.map(market =>
                                        renderMarketRow(market)
                                    )
                                )}

                                {sortedAdditionalMarkets.length > 0 &&
                                    !hasSelection && (
                                        <Box sx={{ mt: 1 }}>
                                            <Box
                                                onClick={() =>
                                                    setShowAdditionalMarkets(
                                                        v => !v
                                                    )
                                                }
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.75,
                                                    cursor: 'pointer',
                                                    py: 0.75,
                                                    px: 0.5,
                                                    borderRadius: 1,
                                                    '&:hover': {
                                                        bgcolor: 'action.hover',
                                                    },
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '11px',
                                                        color: 'text.disabled',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {
                                                        sortedAdditionalMarkets.length
                                                    }{' '}
                                                    additional market
                                                    {sortedAdditionalMarkets.length !==
                                                        1
                                                        ? 's'
                                                        : ''}
                                                </Typography>
                                                {showAdditionalMarkets ? (
                                                    <ExpandLessIcon
                                                        sx={{
                                                            fontSize: 14,
                                                            color: 'text.disabled',
                                                        }}
                                                    />
                                                ) : (
                                                    <ExpandMoreIcon
                                                        sx={{
                                                            fontSize: 14,
                                                            color: 'text.disabled',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            {showAdditionalMarkets &&
                                                sortedAdditionalMarkets.map(
                                                    market =>
                                                        renderMarketRow(
                                                            market,
                                                            alpha(
                                                                theme.palette
                                                                    .text
                                                                    .disabled,
                                                                0.5
                                                            )
                                                        )
                                                )}
                                        </Box>
                                    )}
                            </Box>
                        );
                    })()}

                    {eventData && (
                        <EventsPanel
                            eventContext={eventParam ?? null}
                            hoveredEvent={panelHoveredEvent}
                            onHoverChange={setPanelHoveredEvent}
                            selectedEvent={null}
                            onSelectEvent={handlePanelSelectEvent}
                            topMinHeight={500}
                            bottomMinHeight={250}
                        />
                    )}
                </Box>

                {/* Right sidebar */}
                <Box
                    sx={{
                        display: { xs: 'none', lg: 'block' },
                        position: 'sticky',
                        top: 0,
                        height: '100vh',
                        overflowY: 'hidden',
                        overscrollBehavior: 'contain',
                        borderLeft: 1,
                        borderColor: 'divider',
                    }}
                >
                    <SidebarShell
                        panels={rightPanels}
                        activePanel={rightPanel}
                        onPanelChange={setRightPanel}
                    />
                </Box>
            </Box>
        </SignalInteractionContext.Provider>
    );
}

// ---------------------------------------------------------------------------
// EventHeader
// ---------------------------------------------------------------------------

function EventHeader({
    title,
    image,
    marketCount,
    totalVolume,
    tags,
    endDate,
    isOpen,
    loading,
    slug,
    description,
    liveConnected,
    eventId,
}: {
    title?: string | null;
    image?: string | null;
    marketCount: number;
    totalVolume: number;
    tags: string[];
    endDate?: string | null;
    isOpen: boolean;
    loading: boolean;
    slug?: string | null;
    description?: string | null;
    liveConnected?: boolean;
    eventId?: string | null;
}) {
    const theme = useTheme();
    const daysRemaining = endDate ? formatDaysRemaining(endDate) : null;
    const [descOpen, setDescOpen] = useState(false);

    return (
        <Box sx={{ mb: 2 }}>
            {loading ? (
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Skeleton variant="rounded" width={64} height={64} />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton width="60%" height={28} />
                        <Skeleton width="40%" height={18} sx={{ mt: 0.5 }} />
                    </Box>
                </Box>
            ) : (
                <>
                    <Box
                        sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}
                    >
                        {image && (
                            <Box
                                component="img"
                                src={image}
                                sx={{
                                    width: 64,
                                    height: 64,
                                    borderRadius: '8px',
                                    objectFit: 'cover',
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    mb: 0.75,
                                    minWidth: 0,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '20px',
                                        fontWeight: 700,
                                        lineHeight: 1.3,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {title || 'Event'}
                                </Typography>
                                {slug && (
                                    <Box
                                        component="a"
                                        href={`https://polymarket.com/event/${slug}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: 'text.secondary',
                                            flexShrink: 0,
                                            '&:hover': {
                                                color: 'text.primary',
                                            },
                                        }}
                                    >
                                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                                    </Box>
                                )}
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Badge
                                    color={
                                        isOpen
                                            ? theme.palette.success.main
                                            : theme.palette.text.secondary
                                    }
                                >
                                    {isOpen ? 'Open' : 'Closed'}
                                </Badge>
                                {liveConnected && (
                                    <Badge
                                        color={theme.palette.success.main}
                                        bg={alpha(
                                            theme.palette.success.main,
                                            0.1
                                        )}
                                        sx={{ gap: 0.5 }}
                                    >
                                        <Box
                                            sx={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: '50%',
                                                bgcolor:
                                                    theme.palette.success.main,
                                                animation: 'pulse 2s infinite',
                                                '@keyframes pulse': {
                                                    '0%, 100%': { opacity: 1 },
                                                    '50%': { opacity: 0.4 },
                                                },
                                            }}
                                        />
                                        Live
                                    </Badge>
                                )}
                                <MetaDot>
                                    {marketCount} market
                                    {marketCount !== 1 ? 's' : ''}
                                </MetaDot>
                                <Separator />
                                <MetaDot
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontWeight: 600,
                                    }}
                                >
                                    {formatVolume(totalVolume)} vol
                                </MetaDot>
                                {daysRemaining && (
                                    <>
                                        <Separator />
                                        <MetaDot>{daysRemaining}</MetaDot>
                                    </>
                                )}
                                {description && (
                                    <>
                                        <Separator />
                                        <Typography
                                            onClick={() => setDescOpen(v => !v)}
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                fontSize: '12px',
                                                color: 'text.secondary',
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                '&:hover': {
                                                    color: 'text.primary',
                                                },
                                            }}
                                        >
                                            <InfoOutlinedIcon
                                                sx={{ fontSize: 14 }}
                                            />
                                            {descOpen
                                                ? 'Hide details'
                                                : 'Resolution details'}
                                            {descOpen ? (
                                                <ExpandLessIcon
                                                    sx={{ fontSize: 14 }}
                                                />
                                            ) : (
                                                <ExpandMoreIcon
                                                    sx={{ fontSize: 14 }}
                                                />
                                            )}
                                        </Typography>
                                    </>
                                )}
                                {eventId && (
                                    <EventSubscribeBell
                                        eventId={eventId}
                                        eventTitle={title ?? 'Event'}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                    {description && (
                        <Collapse in={descOpen}>
                            <Box
                                sx={{
                                    mt: 1,
                                    px: 2,
                                    py: 1.5,
                                    borderRadius: 1,
                                    bgcolor: alpha(
                                        theme.palette.text.primary,
                                        0.03
                                    ),
                                    border: 1,
                                    borderColor: 'divider',
                                }}
                            >
                                <LinkedText text={description} />
                            </Box>
                        </Collapse>
                    )}
                </>
            )}
        </Box>
    );
}

// Small helpers to avoid repeated sx objects in EventHeader meta row
function MetaDot({ children, sx }: { children: React.ReactNode; sx?: object }) {
    return (
        <Typography sx={{ fontSize: '12px', color: 'text.secondary', ...sx }}>
            {children}
        </Typography>
    );
}

function Separator() {
    return (
        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
            ·
        </Typography>
    );
}

// ---------------------------------------------------------------------------
// MarketRow
// ---------------------------------------------------------------------------

function MarketRow({
    market,
    label,
    color,
    showImage,
    timeRange,
    isSelected,
    isSingleMarket,
    isMultiMarket,
    totalVolume,
    onToggle,
    highlightedAccounts,
    onToggleAccount,
    loadingAccounts,
    accountColorMap,
    tradesByAccount,
    resolvedVolume,
    liveProb,
    liveVolume,
    liveTraderDeltas,
    convictionCircles,
}: {
    market: EventMarketDetail;
    label: string;
    color: string;
    showImage: boolean;
    timeRange: [Date, Date] | null;
    isSelected: boolean;
    isSingleMarket: boolean;
    isMultiMarket: boolean;
    totalVolume: number;
    onToggle: () => void;
    highlightedAccounts: Set<string>;
    onToggleAccount: (id: string) => void;
    loadingAccounts: Set<string>;
    accountColorMap: Map<string, string>;
    tradesByAccount: Map<string, MarketTrade[]>;
    resolvedVolume: number;
    liveProb?: number;
    liveVolume?: number;
    liveTraderDeltas?: Map<
        string,
        { buyCount: number; sellCount: number; volume: number }
    >;
    convictionCircles?: RowConvictionCircle[];
}) {
    const theme = useTheme();
    const prob = liveProb ?? market.current_yes_prob ?? 0;
    const vol = resolvedVolume + (liveVolume ?? 0);
    const volShare = totalVolume > 0 ? vol / totalVolume : 0;

    const probFlash = useValueFlash(prob, liveProb);
    const volFlash = useValueFlash(vol, liveVolume);
    const probFlashSx =
        probFlash === 'up'
            ? VALUE_FLASH_UP_SX
            : probFlash === 'down'
                ? VALUE_FLASH_DOWN_SX
                : {};
    const volFlashSx =
        volFlash === 'up'
            ? VALUE_FLASH_UP_SX
            : volFlash === 'down'
                ? VALUE_FLASH_DOWN_SX
                : {};

    // Stable flash keys so the animation re-triggers on each new flash
    const probFlashCountRef = useRef(0);
    const wasProbFlashingRef = useRef(false);
    if (probFlash && !wasProbFlashingRef.current)
        probFlashCountRef.current += 1;
    wasProbFlashingRef.current = !!probFlash;

    const volFlashCountRef = useRef(0);
    const wasVolFlashingRef = useRef(false);
    if (volFlash && !wasVolFlashingRef.current) volFlashCountRef.current += 1;
    wasVolFlashingRef.current = !!volFlash;

    return (
        <SectionCard sx={{ mb: 1, borderLeft: `3px solid ${color}` }}>
            <Box
                onClick={onToggle}
                sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    '&:hover': { bgcolor: 'action.hover' },
                    transition: 'background 0.15s',
                    bgcolor: isSelected ? alpha(color, 0.04) : undefined,
                }}
            >
                {!isSingleMarket ? (
                    <Checkbox
                        checked={isSelected}
                        size="small"
                        sx={{
                            p: 0,
                            color: alpha(theme.palette.text.disabled, 0.4),
                            '&.Mui-checked': { color },
                        }}
                        onClick={e => e.stopPropagation()}
                        onChange={onToggle}
                    />
                ) : (
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: color,
                            flexShrink: 0,
                        }}
                    />
                )}

                {showImage && market.image && (
                    <Box
                        component="img"
                        src={market.image}
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '4px',
                            objectFit: 'cover',
                            flexShrink: 0,
                        }}
                    />
                )}

                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        overflow: 'hidden',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: isMultiMarket ? '14px' : '13px',
                            fontWeight: isMultiMarket ? 700 : 600,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flexShrink: 1,
                            minWidth: 0,
                        }}
                    >
                        {label}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            key={`prob-${probFlashCountRef.current}`}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 0.5,
                                borderRadius: 0.5,
                                ...probFlashSx,
                            }}
                        >
                            <ProbBar prob={prob} color={color} width={72} />
                        </Box>
                        <Typography
                            key={`vol-${volFlashCountRef.current}`}
                            sx={{
                                fontSize: '11px',
                                color: 'text.secondary',
                                fontFamily: 'monospace',
                                px: 0.5,
                                borderRadius: 0.5,
                                whiteSpace: 'nowrap',
                                ...volFlashSx,
                            }}
                        >
                            {formatVolume(vol)} ({(volShare * 100).toFixed(0)}%)
                        </Typography>
                        {market.closed && (
                            <Badge color={theme.palette.text.disabled}>
                                Closed
                            </Badge>
                        )}
                    </Box>
                </Box>

                {convictionCircles && convictionCircles.length > 0 && (
                    <ConvictionCircles circles={convictionCircles} />
                )}

                {!isSingleMarket && (
                    <IconButton size="small" sx={{ flexShrink: 0 }}>
                        {isSelected ? (
                            <ExpandLessIcon fontSize="small" />
                        ) : (
                            <ExpandMoreIcon fontSize="small" />
                        )}
                    </IconButton>
                )}
            </Box>

            <Collapse in={isSelected} timeout={300}>
                <Box
                    sx={{
                        borderTop: 1,
                        borderColor: 'divider',
                        p: 2,
                        bgcolor: alpha(theme.palette.background.default, 0.5),
                        maxHeight: '60vh',
                        ...hoverScrollbar,
                    }}
                >
                    <TraderInsights
                        market={market}
                        color={color}
                        startDate={timeRange?.[0] ?? null}
                        endDate={timeRange?.[1] ?? null}
                        highlightedAccounts={highlightedAccounts}
                        onToggleAccount={onToggleAccount}
                        accountColorMap={accountColorMap}
                        loadingAccounts={loadingAccounts}
                        tradesByAccount={tradesByAccount}
                        liveTraderDeltas={liveTraderDeltas}
                    />
                </Box>
            </Collapse>
        </SectionCard>
    );
}

// ---------------------------------------------------------------------------
// LinkedText
// ---------------------------------------------------------------------------

function LinkedText({ text }: { text: string }) {
    const parts = text.split(/(https?:\/\/[^\s,)]+)/g);
    return (
        <Typography
            sx={{
                fontSize: '13px',
                lineHeight: 1.6,
                color: 'text.secondary',
                whiteSpace: 'pre-line',
            }}
        >
            {parts.map((part, i) =>
                /^https?:\/\//.test(part) ? (
                    <Box
                        key={i}
                        component="a"
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                            wordBreak: 'break-all',
                        }}
                    >
                        {part}
                    </Box>
                ) : (
                    <span key={i}>{part}</span>
                )
            )}
        </Typography>
    );
}
